/* istanbul ignore file */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Tabs } from '@interstate/components/Tabs';
import { Box } from '@interstate/components/Box';
import { TextInput } from '@interstate/components/TextInput';

import * as proposalActionCreators from '@makemydeal/dr-dash-store/actions/proposalActionCreators';
import * as proposalSelectors from '@makemydeal/dr-dash-store/selectors/proposalSelectors';

import { GeneralTab } from './GeneralTab';
import { DisplayTab } from './DisplayTab';
import { TEXT_INPUT_MAX_LENGTH } from './constants';
import { useProposalForm } from './useProposalForm';
import type { ProposalFormPayload } from './useProposalForm';
import { useHiddenProposalKeys } from './useHiddenProposalKeys';
import { hasFieldLabel, hasDisplayLabel } from './utils';
import { useDataLoadingState } from '../../utils/useDataLoadingState';
import { LoadingSection } from '@makemydeal/dr-shared-components';
import { Toast } from '../../common/DataLoadingToast';
import type { ToastType } from '../../common/DataLoadingToast/Toast';

export interface CreateProposalHandle {
    getFormData: () => ProposalFormPayload;
}

const noop = () => {};

type CreatePrintingProposalProps = {
    shouldDisableSubmit?: (hasError: boolean) => void;
    setView: (view: 'list' | 'create' | 'edit' | 'preview' | 'none') => void;
    setEditMode?: (isEditMode: boolean) => void;
    isEditMode?: boolean;
};

export const CreatePrintingProposal = forwardRef<CreateProposalHandle, CreatePrintingProposalProps>(
    ({ shouldDisableSubmit: setHasValidationError = noop, setView, isEditMode, setEditMode = noop }, ref) => {
        const dispatch = useDispatch();
        const printingProposals = useSelector(proposalSelectors.getProposalList);
        const { hiddenFieldKeys, hiddenDisplayKeys } = useHiddenProposalKeys();

        // In create mode, fetch the default printing proposal to use as a template.
        useEffect(() => {
            if (!isEditMode) {
                const defaultProposal = printingProposals.find((p) => p.isDefault && p.type !== 'fourSquare');
                if (defaultProposal) {
                    dispatch(proposalActionCreators.getProposal(defaultProposal.id));
                }
            }
        }, [isEditMode]);

        const fieldKeyFilter = useCallback((key: string) => hasFieldLabel(key) && !hiddenFieldKeys.has(key), [hiddenFieldKeys]);
        const displayKeyFilter = useCallback(
            (key: string) => hasDisplayLabel(key) && !hiddenDisplayKeys.has(key),
            [hiddenDisplayKeys]
        );

        const form = useProposalForm({ isEditMode, fieldKeyFilter, displayKeyFilter });

        useDataLoadingState({
            isLoading: form.isCreatingProposal,
            isError: form.hasCreationError,
            isLoaded: form.isCreationSuccess,
            onLoaded: () => {
                setEditMode(!!isEditMode);
                setView('preview');
            }
        });

        useDataLoadingState({
            isLoading: form.isUpdatingProposal,
            isError: form.hasUpdateError,
            isLoaded: form.isUpdateSuccess,
            onLoaded: () => {
                setEditMode(!!isEditMode);
                setView('preview');
            }
        });

        useEffect(() => {
            setHasValidationError(form.hasError);
        }, [form.hasError]);

        useImperativeHandle(
            ref,
            () => ({
                getFormData: form.getFormData
            }),
            [form.getFormData]
        );

        const tabs = useMemo(
            () => [
                {
                    label: 'General',
                    active: form.activeTab === 'general',
                    value: 'general',
                    component: (
                        <GeneralTab
                            fieldConfiguration={form.fieldConfiguration}
                            onDisplayNameChange={form.handleDisplayNameChangeForFieldConfigItem}
                            onBoldChange={form.handleBoldChangeForFieldConfigItem}
                            onActiveChange={form.handleActiveChangeForFieldConfigItem}
                            onOrderChange={form.handleOrderChangeForFieldConfig}
                        />
                    )
                },
                {
                    label: 'Display',
                    active: form.activeTab === 'display',
                    value: 'display',
                    component: (
                        <DisplayTab
                            displayConfiguration={form.displayConfiguration}
                            onDisplayActiveChange={form.handleActiveChangeForDisplayConfigItem}
                            onDisplayValueChange={form.handleValueChangeForDisplayConfigItem}
                            onDisplayNameChange={form.handleDisplayNameChangeForDisplayConfigItem}
                            onDollarValueRoundingChange={form.handleDollarValueRoundingChange}
                            onFontSizeAdjustmentChange={form.handleFontSizeAdjustmentChange}
                        />
                    )
                }
            ],
            [
                form.activeTab,
                form.fieldConfiguration,
                form.displayConfiguration,
                form.handleDisplayNameChangeForFieldConfigItem,
                form.handleBoldChangeForFieldConfigItem,
                form.handleActiveChangeForFieldConfigItem,
                form.handleOrderChangeForFieldConfig,
                form.handleActiveChangeForDisplayConfigItem,
                form.handleValueChangeForDisplayConfigItem,
                form.handleDisplayNameChangeForDisplayConfigItem,
                form.handleDollarValueRoundingChange,
                form.handleFontSizeAdjustmentChange
            ]
        );

        const getErrorMessage = (type: ToastType) => {
            if (isEditMode && type === 'error' && form.hasUpdateError) {
                return form.updateErrorMessage || 'Error saving printing proposal.';
            }
            if (!isEditMode && type === 'error' && form.hasCreationError) {
                return form.creationErrorMessage || 'Error creating printing proposal.';
            }
            return undefined;
        };

        return (
            <Box data-testid="create-proposal-component" sx={{ display: 'flex', flexDirection: 'column' }}>
                <LoadingSection
                    loading={
                        form.isLoadingProposal ||
                        form.isCreatingProposal ||
                        form.isUpdatingProposal ||
                        (!isEditMode && !form.proposalToEdit && !form.isProposalError)
                    }
                />
                <Box mb={2}>
                    <Toast
                        isLoading={form.isCreatingProposal || form.isUpdatingProposal}
                        isLoaded={form.isCreationSuccess || form.isUpdateSuccess}
                        isError={form.hasCreationError || form.hasUpdateError}
                        getMessage={getErrorMessage}
                    />
                </Box>
                <Box sx={{ mb: 2 }}>
                    <TextInput
                        label="Proposal Name"
                        name="proposal-name"
                        required
                        maxLength={TEXT_INPUT_MAX_LENGTH}
                        customErrorMessages={{
                            exceedsMaxLength: `Value exceeds ${TEXT_INPUT_MAX_LENGTH} characters.`
                        }}
                        value={form.proposalName}
                        onChange={(e) => form.setProposalName(e.target.value)}
                        size="small"
                        data-testid="proposal-name-input"
                    />
                </Box>
                <Tabs
                    id="create-proposal-tabs"
                    data-testid="create-proposal-tabs"
                    onActivate={(tab) => {
                        form.setActiveTab(tab);
                    }}
                    tabs={tabs}
                />
            </Box>
        );
    }
);

CreatePrintingProposal.displayName = 'CreatePrintingProposal';
